import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { getInventoryDate, useNextOrder } from '@hungryroot/orders';
import { StatusCode } from '@hungryroot/rest';
import {
  getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig,
  getPricingServiceCustomerProductSubscriptionCustomerConfigPath,
  type PricingServiceCustomerProductSubscriptionCustomerConfigParams,
} from '@hungryroot/rest/api/v2/pricing_service/customers/[id]/product_subscription_customer_config/get';

const timeLimit = 15 * 60 * 1000;

function useQueryCustomerProductSubscriptionConfig(
  params: PricingServiceCustomerProductSubscriptionCustomerConfigParams,
  options?: Pick<UseQueryOptions, 'enabled'>
) {
  return useQuery({
    queryKey: [
      getPricingServiceCustomerProductSubscriptionCustomerConfigPath(params),
    ],
    queryFn: async () => {
      const resp =
        await getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig(
          params
        );

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    retry: false,
    enabled: options?.enabled,
    gcTime: timeLimit,
    staleTime: timeLimit,
  });
}

export function useGetCustomerProductSubscriptionConfig(
  { containerId }: { containerId?: number } = {},
  options?: Pick<UseQueryOptions, 'enabled'>
) {
  const { enabled } = options || {};

  const { order: nextEditableOrder } = useNextOrder({ enabled });

  const cstomerProductSubscriptionConfigData =
    useQueryCustomerProductSubscriptionConfig(
      {
        containerId,
        origin: nextEditableOrder?.origin,
        inventory_date:
          nextEditableOrder !== undefined
            ? getInventoryDate(nextEditableOrder)
            : '',
      },
      {
        enabled: enabled !== false && nextEditableOrder !== undefined,
      }
    );

  return cstomerProductSubscriptionConfigData;
}
